import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface HelpNavigationTabsProps {
  className?: string;
}

export default function HelpNavigationTabs({
  className = "",
}: HelpNavigationTabsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const navigationItems = [
    {
      value: "abrir-chamado",
      label: "Abrir chamado",
      route: "/cliente/ajuda/abrir-chamado",
    },
    {
      value: "chamados-abertos",
      label: "Chamados abertos",
      route: "/cliente/ajuda/chamados-abertos",
    },
    {
      value: "historico-de-chamados",
      label: "Histórico de chamados",
      route: "/cliente/ajuda/historico-de-chamados",
    },
    {
      value: "perguntas-frequentes",
      label: "Perguntas frequentes",
      route: "/cliente/ajuda/perguntas-frequentes",
    },
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes("abrir-chamado")) return "abrir-chamado";
    if (path.includes("chamados-abertos")) return "chamados-abertos";
    if (path.includes("historico-de-chamados")) return "historico-de-chamados";
    if (path.includes("perguntas-frequentes")) return "perguntas-frequentes";
    return "abrir-chamado";
  };

  const activeItem = navigationItems.find(
    (item) => item.value === getActiveTab()
  );

  const handleTabChange = (value: string) => {
    const routes = {
      "abrir-chamado": "/cliente/ajuda/abrir-chamado",
      "chamados-abertos": "/cliente/ajuda/chamados-abertos",
      "historico-de-chamados": "/cliente/ajuda/historico-de-chamados",
      "perguntas-frequentes": "/cliente/ajuda/perguntas-frequentes",
    };
    navigate(routes[value as keyof typeof routes]);
  };

  const handleMobileNavigation = (route: string) => {
    navigate(route);
    setIsOpen(false);
  };

  return (
    <>
      {/* Desktop - Tabs */}
      <Tabs
        value={getActiveTab()}
        onValueChange={handleTabChange}
        className={cn("w-full hidden md:block", className)}
      >
        <div className="overflow-x-auto">
          <TabsList className="inline-flex gap-2 whitespace-nowrap p-1 bg-transparent">
            <TabsTrigger
              value="abrir-chamado"
              className="rounded-full bg-gray-100 px-5 py-2 text-sm font-medium text-gray-700 data-[state=active]:bg-n-green-status data-[state=active]:text-n-green"
            >
              Abrir chamado
            </TabsTrigger>
            <TabsTrigger
              value="chamados-abertos"
              className="rounded-full bg-gray-100 px-5 py-2 text-sm font-medium text-gray-700 data-[state=active]:bg-n-green-status data-[state=active]:text-n-green"
            >
              Chamados abertos
            </TabsTrigger>
            <TabsTrigger
              value="historico-de-chamados"
              className="rounded-full bg-gray-100 px-5 py-2 text-sm font-medium text-gray-700 data-[state=active]:bg-n-green-status data-[state=active]:text-n-green"
            >
              Histórico de chamados
            </TabsTrigger>
            <TabsTrigger
              value="perguntas-frequentes"
              className="rounded-full bg-gray-100 px-5 py-2 text-sm font-medium text-gray-700 data-[state=active]:bg-n-green-status data-[state=active]:text-n-green"
            >
              Perguntas frequentes
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* Mobile - Dropdown Menu */}
      <div className={cn("w-full md:hidden relative", className)}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
        >
          <span>{activeItem?.label || "Selecione uma opção"}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>

        {isOpen && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown Menu */}
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
              {navigationItems.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handleMobileNavigation(item.route)}
                  className={cn(
                    "w-full text-left px-4 py-3 text-sm font-medium transition-colors",
                    getActiveTab() === item.value
                      ? "bg-n-green text-white"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
